// Geometry for straightening photographed pages. Pure functions, so they can be tested without a browser.

export type Pt = [number, number];

export function orderCorners(pts: Pt[]): Pt[] {
  const s = (p: Pt) => p[0] + p[1], d = (p: Pt) => p[0] - p[1];
  const tl = pts.reduce((a, b) => (s(b) < s(a) ? b : a));
  const br = pts.reduce((a, b) => (s(b) > s(a) ? b : a));
  const tr = pts.reduce((a, b) => (d(b) > d(a) ? b : a));
  const bl = pts.reduce((a, b) => (d(b) < d(a) ? b : a));
  return [tl, tr, br, bl];
}
export function solveHomography(from: Pt[], to: Pt[]): number[] {
  const A: number[][] = [], B: number[] = [];
  for (let i = 0; i < 4; i++) {
    const [u, v] = from[i], [x, y] = to[i];
    A.push([u, v, 1, 0, 0, 0, -u * x, -v * x]); B.push(x);
    A.push([0, 0, 0, u, v, 1, -u * y, -v * y]); B.push(y);
  }
  const n = 8;
  for (let c = 0; c < n; c++) {
    let p = c; for (let r = c + 1; r < n; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
    [A[c], A[p]] = [A[p], A[c]]; [B[c], B[p]] = [B[p], B[c]];
    const piv = A[c][c] || 1e-12;
    for (let r = c + 1; r < n; r++) {
      const f = A[r][c] / piv; if (!f) continue;
      for (let k = c; k < n; k++) A[r][k] -= f * A[c][k];
      B[r] -= f * B[c];
    }
  }
  const h: number[] = new Array(n).fill(0);
  for (let r = n - 1; r >= 0; r--) { let s = B[r]; for (let k = r + 1; k < n; k++) s -= A[r][k] * h[k]; h[r] = s / (A[r][r] || 1e-12); }
  return h;
}
export function convexHull(input: Pt[]): Pt[] {
  const pts = input.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: Pt, a: Pt, b: Pt) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lo: Pt[] = [], hi: Pt[] = [];
  for (const p of pts) { while (lo.length >= 2 && cross(lo[lo.length - 2], lo[lo.length - 1], p) <= 0) lo.pop(); lo.push(p); }
  for (let i = pts.length - 1; i >= 0; i--) { const p = pts[i]; while (hi.length >= 2 && cross(hi[hi.length - 2], hi[hi.length - 1], p) <= 0) hi.pop(); hi.push(p); }
  return lo.slice(0, -1).concat(hi.slice(0, -1));
}
export const quadArea = (q: Pt[]): number => Math.abs(q.reduce((a, p, i) => { const n = q[(i + 1) % 4]; return a + p[0] * n[1] - n[0] * p[1]; }, 0)) / 2;
